/**
 * TABLA EN LA CONVERSACIÓN (`sk-prosetable`, 2026-10-27): la única
 * estructura que el asesor puede escribir en su prosa — una tabla GFM con
 * cuatro filas o más de lo mismo —, pintada con la pieza del sistema. Se
 * LEE como un párrafo: sin orden, sin selección, sin acciones.
 *
 * `ProseBlocks` es lo que usan el Despacho y el inspector del Studio: los
 * bloques de un turno (`bloques` de prosa.ts) con su envase por párrafo,
 * y desde el 2026-10-30 también la lista numerada (`ProseList`).
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { cx } from "../cx";
import { bloques, trozos, type TablaProsa } from "./prosa";
import { Trozos } from "./Trozos";
import { ProseList } from "./ProseList";

/** Una tabla del asesor, siempre con nombre. Si no cabe, scroll horizontal dentro de la pieza, y entonces región enfocable (para moverlo con teclado). */
export function ProseTable({ tabla, className }: { tabla: TablaProsa; className?: string | undefined }) {
  const ref = useRef<HTMLDivElement>(null);
  const [desborda, setDesborda] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const medir = (): void => setDesborda(el.scrollWidth > el.clientWidth + 1);
    medir();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    // la tabla también: si llega la fuente web y la ensancha, el contenedor no cambia
    if (el.firstElementChild !== null) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [tabla]);
  // el nombre, en llano: sin marcas de énfasis ni cabeceras vacías
  const nombre = `Tabla: ${tabla.cabecera.map((c) => trozos(c).map((t) => t.texto).join("").trim()).filter((c) => c !== "").join(", ")}`;
  const num = (j: number): string | undefined => (tabla.cifras[j] === true ? "sk-prosetable-num" : undefined);
  return (
    <div
      ref={ref} className={cx("sk-prosetable", className)}
      // solo si desborda es región enfocable (para moverla con teclado): si no,
      // un punto de referencia y un tabulador vacíos por cada tabla
      {...(desborda && { role: "region", "aria-label": nombre, tabIndex: 0 })}
    >
      <table aria-label={nombre}>
        <thead>
          <tr>{tabla.cabecera.map((c, j) => <th key={j} scope="col" className={num(j)}><Trozos texto={c} /></th>)}</tr>
        </thead>
        <tbody>
          {tabla.filas.map((f, i) => (
            <tr key={i}>
              {f.map((c, j) => (j === 0
                ? <th key={j} scope="row"><Trozos texto={c} /></th>
                : <td key={j} className={num(j)}><Trozos texto={c} /></td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** La tabla mientras se escribe: no se enseña a medias ni con barras crudas. */
export function ProseTablePending({ label = "preparando la tabla…" }: { label?: ReactNode }) {
  return (
    <div className="sk-prosetable sk-prosetable--pending">
      <span className="sk-skeleton" aria-hidden="true" />
      <span className="sk-skeleton" aria-hidden="true" />
      <span className="sk-skeleton" aria-hidden="true" />
      <span className="sk-prosetable-wait">{label}</span>
    </div>
  );
}

/**
 * Los bloques de un turno del asesor: párrafos con su énfasis y, si las
 * hay, la tabla y la lista numerada. `enCurso` mientras la respuesta llega a trozos.
 * `parrafo` = el envase de cada párrafo (por defecto `<p>`; el Studio pone
 * su `Prose`).
 */
export function ProseBlocks({ texto, enCurso = false, parrafo }: {
  texto: string;
  enCurso?: boolean | undefined;
  parrafo?: ((contenido: ReactNode, key: number) => ReactNode) | undefined;
}) {
  return (
    <>
      {bloques(texto, { enCurso }).map((b, i) => {
        if (b.tipo === "tabla") return <ProseTable key={i} tabla={b.tabla} />;
        if (b.tipo === "tabla-en-curso") return <ProseTablePending key={i} />;
        if (b.tipo === "lista") return <ProseList key={i} items={b.items} />;
        const dentro = <Trozos texto={b.texto} />;
        return parrafo !== undefined ? parrafo(dentro, i) : <p key={i}>{dentro}</p>;
      })}
    </>
  );
}
